import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerQuoteFonts } from "@/lib/pdfFont";
import { NUM, fmtDateTime, unitLabel, docFileName } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { logoAspect, loadCompanyLogo } from "@/lib/branding";
import { loadIcons } from "@/lib/pdfIcons";
import { loadBrandLogos } from "@/lib/brandLogos";
import { itemDisplayName } from "@/components/QuoteTemplate";
import { MODE_LABEL, PAY_LABEL, r2 } from "@/lib/pos";

// Satış fişi — vektörel PDF (önizleme = indirilen = yazdırılan). Teklif şablonuyla aynı lacivert/altın aile; yazıcı dostu (büyük dolgu yok).
export const receiptBelgeNo = (s) => `HS-${(s.id || "").slice(0, 6).toUpperCase()}`;
export const receiptFilename = (s) => docFileName(s.cari_contact_title || s.title || "Satış Fişi", receiptBelgeNo(s));
// Kayıtlı kalemin müşteriye gösterilen KDV dahil birim fiyatı (yeni kayıtlarda unit_gross saklanır; eskilerde net×KDV)
export const unitGrossOf = (it) => (it.unit_gross != null ? r2(it.unit_gross) : r2((Number(it.unit_net) || 0) * (1 + (Number(it.kdv_rate) || 0) / 100)));
export const DELIVERY_TEXT = "Yukarıda belirtilen ürünler eksiksiz, sağlam ve tam olarak teslim edilmiştir. Ürünler sayılarak / kontrol edilerek teslim alınmıştır.";

const NAVY = [15, 39, 69];
const NAVY_HEX = "#0F2745";
const GOLD = [242, 183, 5];
const GOLD_D = [196, 144, 0];
const INK = [30, 41, 59];
const SLATE = [100, 116, 139];
const MUTED = [148, 163, 184];
const LINE = [222, 228, 236];
const WASH = [245, 247, 250];
const WHITE = [255, 255, 255];
const RED = [185, 28, 28];
const money = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;
const titleTr = (s) => String(s || "").toLocaleLowerCase("tr").replace(/(^|[\s/,.\-(])(\S)/g, (m, a, b) => a + b.toLocaleUpperCase("tr"));
const FIT_STEPS = [[8.4, false], [8, false], [7.5, false], [7, true], [6.5, true], [6, true], [5.5, true], [5, true], [4.5, true]];

export const buildReceiptPdf = async (s, opts = {}) => {
  const cols = { code: false, brand: false, discount: false, logos: false, ...(opts.cols || {}) };
  const vatNote = opts.vatNote ? String(opts.vatNoteText || "").trim() : "";   // GENEL TOPLAM altındaki KDV notu: isteğe bağlı, düzenlenebilir
  const [logo, icons, brands] = await Promise.all([loadCompanyLogo(), loadIcons(NAVY_HEX), cols.logos ? loadBrandLogos() : Promise.resolve([])]);
  const AR = logoAspect();
  const logos = (brands || []).filter(Boolean);
  const items = s.items || [];
  const belgeNo = receiptBelgeNo(s);
  const dt = fmtDateTime(s.created_at);
  const modeLabel = s.mode && s.mode !== "genel" ? (MODE_LABEL[s.mode] || s.mode) : "";
  const payLabel = s.payment_method === "karma" ? `Karma (${(s.payments || []).length} parça)` : PAY_LABEL[s.payment_method] || "Nakit";

  // Genel iskonto / yukarı yuvarlama kalemlere ORANSAL dağıtılır → fişte tek, tutarlı fiyatlar
  const rawLineTotals = items.map((it) => (it.line_total != null ? Number(it.line_total) : r2((Number(it.line_net) || 0) + (Number(it.line_kdv) || 0))));
  const baseTotal = Number(s.computed_total) || rawLineTotals.reduce((a, b) => a + b, 0);
  const grandTotal = Number(s.grand_total || 0);
  const factor = s.override_applied && baseTotal > 0 ? grandTotal / baseTotal : 1;
  const lineTotals = rawLineTotals.map((v) => r2(v * factor));
  if (factor !== 1 && lineTotals.length) {
    const diff = r2(grandTotal - lineTotals.reduce((a, b) => a + b, 0));
    if (Math.abs(diff) >= 0.01) { let mi = 0; lineTotals.forEach((v, i) => { if (v > lineTotals[mi]) mi = i; }); lineTotals[mi] = r2(lineTotals[mi] + diff); }
  }
  const unitGrossAdj = items.map((it, i) => { const q = Number(it.quantity) || 0; return q > 0 ? r2(lineTotals[i] / q) : unitGrossOf(it); });

  // Ödeme dağılımı · nakit/para üstü · iade özeti
  const payParts = (s.payments || []).filter((p) => Number(p.amount) > 0);
  const payRows = payParts.length > 1 || payParts.some((p) => p.method === "veresiye") ? payParts.map((p) => [PAY_LABEL[p.method] || p.method, money(p.amount), p.method === "veresiye"]) : [];
  const cashReceived = Number(s.cash_received || 0);
  const changeGiven = Number(s.change_given != null ? s.change_given : (cashReceived > 0 ? cashReceived - grandTotal : 0));
  const cashRows = cashReceived > 0.005 ? [["Alınan Nakit", money(cashReceived)], ["Verilen Para Üstü", money(Math.max(0, changeGiven))]] : [];
  const returnedTotal = Number(s.returned_total || 0);
  const hasReturn = returnedTotal > 0.005;
  const remaining = r2(grandTotal - returnedTotal);
  const retAgg = Object.values((s.returns || []).flatMap((r) => r.lines || []).reduce((m, l) => { const kk = l.name || "-"; (m[kk] = m[kk] || { name: kk, qty: 0 }); m[kk].qty += Number(l.quantity) || 0; return m; }, {}));

  const render = async (base, compact) => {
    const pdf = new jsPDF("p", "mm", "a4");
    const F = await registerQuoteFonts(pdf);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const M = 12, W = pageW - M * 2, FOOT = 12, HEAD1 = compact ? 64 : 71, HEAD2 = 22, STRIP = logos.length ? (compact ? 10 : 12) : 0;
    const k = compact ? 0.82 : 1;
    const drawn = new Set();
    const T = (font, style, size, color) => { pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(...color); };
    const fill = (c) => pdf.setFillColor(...c);
    const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
    const icon = (name, x, y, sz) => { if (icons[name]) { try { pdf.addImage(icons[name], "PNG", x, y, sz, sz); } catch { /* noop */ } } };
    const image = (data, x, y, w, h) => { try { pdf.addImage(data, "PNG", x, y, w, h); return true; } catch { return false; } };
    const shrink = (text, maxW, font, style, size, min) => { let fs = size; T(font, style, fs, INK); while (pdf.getTextWidth(text) > maxW && fs > min) { fs -= 0.3; pdf.setFontSize(fs); } return fs; };
    const ellipsize = (text, maxW) => { let t = String(text || ""); if (pdf.getTextWidth(t) <= maxW) return t; while (t.length > 1 && pdf.getTextWidth(`${t}…`) > maxW) t = t.slice(0, -1); return `${t.trimEnd()}…`; };

    const drawFooter = () => {
      fill(GOLD); pdf.rect(M, pageH - FOOT + 0.6, W, 0.5, "F");
      T(F.head, "normal", 6.4, NAVY); pdf.text(COMPANY.name, M, pageH - FOOT + 5.4);
      T(F.body, "normal", 5.6, SLATE); pdf.text(titleTr(COMPANY.address), M, pageH - FOOT + 8.7);
      T(F.body, "bold", 6, NAVY); pdf.text(`${COMPANY.email}   ·   ${COMPANY.phone}`, pageW - M, pageH - FOOT + 5.4, { align: "right" });
    };

    const drawBrandStrip = () => {
      fill(GOLD); pdf.rect(0, HEAD1 + STRIP, pageW, 0.9, "F");
      if (!STRIP) return;
      stroke(LINE, 0.35); pdf.line(0, HEAD1, pageW, HEAD1);
      const maxW = compact ? 19 : 21, minGap = 3.4;
      let h0 = compact ? 4.6 : 5.2, sizes = [];
      for (; h0 >= 3; h0 -= 0.2) {
        sizes = logos.map((b) => { const ar = b.w / b.h; const w = Math.min(ar * h0, maxW); return { w, h: w / ar }; });
        if (sizes.reduce((a, z) => a + z.w, 0) + minGap * (logos.length - 1) <= W) break;
      }
      const total = sizes.reduce((a, z) => a + z.w, 0);
      const gap = Math.min((W - total) / Math.max(1, logos.length - 1), 10);
      let x = M + (W - total - gap * (logos.length - 1)) / 2;
      const cy = HEAD1 + STRIP / 2;
      logos.forEach((b, i) => {
        image(b.data, x, cy - sizes[i].h / 2, sizes[i].w, sizes[i].h);
        if (i < logos.length - 1) { stroke(LINE, 0.3); pdf.line(x + sizes[i].w + gap / 2, HEAD1 + 3, x + sizes[i].w + gap / 2, HEAD1 + STRIP - 3); }
        x += sizes[i].w + gap;
      });
    };

    const drawHeader = (pageNo) => {
      if (drawn.has(pageNo)) return;
      drawn.add(pageNo);
      if (pageNo === 1) {
        // Sol: büyük logo + ikonlu şirket bilgileri
        const lh = compact ? 19 : 23, lw = Math.min(lh * AR, 92);
        if (!logo || !image(logo, M, 7, lw, lh)) { T(F.head, "bold", 19, NAVY); pdf.text(COMPANY.name, M, 7 + lh * 0.65); }
        const infoFs = compact ? 7.6 : 8.5, step = infoFs * 0.5, availW = 118 - M - 10.8;
        let iy = 7 + lh + (compact ? 7.5 : 9);
        const rows = [["pin", titleTr(COMPANY.address)], ["phone", `${COMPANY.phone}   |   ${COMPANY.salesRep}`], ["mail", COMPANY.email], ["building", `Vergi Dairesi: ${titleTr(COMPANY.taxOffice)}`], ["id", `TCKN: ${COMPANY.tckn}`]];
        rows.forEach(([ic, txt]) => {
          T(F.body, "normal", infoFs, INK);
          const lines = pdf.splitTextToSize(txt, availW).slice(0, 2);
          icon(ic, M + 0.2, iy - 3.6, 4.6);
          pdf.text(lines, M + 7.8, iy, { lineHeightFactor: 1.42 });
          iy += lines.length * step + 1.6;
        });
        stroke(LINE, 0.35); pdf.line(118, 10, 118, HEAD1 - 7);
        // Sağ: SATIŞ FİŞİ + metin boyunca altın çizgi + belge bilgileri (sağa yaslı, etiket/değer)
        const title = "SATIŞ FİŞİ", tfs = compact ? 23 : 26, ty = compact ? 26 : 28;
        T(F.head, "bold", tfs, NAVY);
        const tw = pdf.getTextWidth(title) + 0.8 * (title.length - 1);
        pdf.text(title, pageW - M, ty, { align: "right", charSpace: 0.8 });
        fill(GOLD); pdf.rect(pageW - M - tw, ty + 3.2, tw, 1.4, "F");
        let my = ty + 12.5;
        const metaRows = [["BELGE NO", belgeNo], ["TARİH", dt]];
        if (modeLabel) metaRows.push(["SATIŞ TÜRÜ", modeLabel]);
        metaRows.forEach(([l, v]) => {
          T(F.head, "bold", compact ? 8.2 : 9, NAVY); const vw = pdf.getTextWidth(v); pdf.text(v, pageW - M, my, { align: "right" });
          T(F.body, "bold", 5.8, SLATE); pdf.text(l, pageW - M - vw - 3.5, my - 0.2, { align: "right", charSpace: 0.3 });
          my += compact ? 5.4 : 6;
        });
        drawBrandStrip();
      } else {
        const lh = 10, lw = Math.min(lh * AR, 42);
        if (logo) image(logo, M, 5, lw, lh);
        T(F.head, "bold", 13, NAVY); pdf.text("SATIŞ FİŞİ", pageW - M, 11.5, { align: "right", charSpace: 0.6 });
        T(F.head, "bold", 7.4, GOLD_D); pdf.text(belgeNo, pageW - M, 16.5, { align: "right" });
        T(F.body, "normal", 6.3, SLATE); pdf.text(`${s.title || "Perakende Müşteri"}  ·  ${dt}  ·  devam`, pageW - M - pdf.getTextWidth(belgeNo) - 4, 16.5, { align: "right" });
        fill(GOLD); pdf.rect(M, HEAD2 - 1.6, W, 0.9, "F");
      }
      drawFooter();
    };

    // ---------- Bilgi kartı: Müşteri · Ödeme · Kalem · Personel ----------
    drawHeader(1);
    const y0 = HEAD1 + STRIP + (compact ? 4.5 : 6), infoH = compact ? 16 : 18;
    fill(WASH); pdf.roundedRect(M, y0, W, infoH, 3, 3, "F");
    const cells = [["user", "MÜŞTERİ / CARİ", s.title || "Perakende Müşteri", 0.35], ["card", "ÖDEME", payLabel, 0.2], ["list", "KALEM", String(s.item_count || items.length), 0.15], ["staff", "SATIŞ PERSONELİ", s.user_name || "-", 0.3]];
    let cx0 = M;
    cells.forEach(([ic, l, v, fr], i) => {
      const cw = W * fr, bs = 8 * k, by = y0 + (infoH - bs) / 2;
      if (i > 0) { stroke(LINE, 0.4); pdf.line(cx0, y0 + 4, cx0, y0 + infoH - 4); }
      fill(WHITE); pdf.roundedRect(cx0 + 3, by, bs, bs, 2, 2, "F");
      icon(ic, cx0 + 3 + bs * 0.2, by + bs * 0.2, bs * 0.6);
      const tx = cx0 + 3 + bs + 2.6, inner = cw - (tx - cx0) - 3;
      // Etiket hücreye sığmazsa küçülür → yan hücrenin çizgisi/ikonuyla üst üste binmez
      let lfs = 5.8, lcs = 0.3;
      T(F.body, "bold", lfs, SLATE);
      while (pdf.getTextWidth(l) + lcs * (l.length - 1) > inner && lfs > 4.4) { lfs -= 0.2; lcs = 0.15; pdf.setFontSize(lfs); }
      pdf.text(l, tx, y0 + infoH / 2 - 1.8, { charSpace: lcs });
      const fs = shrink(v, inner, F.head, "bold", compact ? 8.4 : 9.2, 6.5);
      T(F.head, "bold", fs, NAVY); pdf.text(ellipsize(v, inner), tx, y0 + infoH / 2 + 4.2);
      cx0 += cw;
    });

    // ---------- Kalem tablosu (KDV sütunu yok; Birim Fiyat = iskonto düşülmüş net) ----------
    const colDefs = [{ key: "no", label: "#", w: 7, align: "center", st: { textColor: SLATE } }];
    if (cols.code) colDefs.push({ key: "code", label: "ÜRÜN KODU", w: 23, align: "center", st: { font: F.mono, fontSize: base - 0.8, textColor: INK } });
    colDefs.push({ key: "name", label: "ÜRÜN / AÇIKLAMA", w: null, align: "left", st: { fontStyle: "bold" } });
    colDefs.push({ key: "qty", label: "MİKTAR", w: 21, align: "center", st: {} });
    if (cols.discount) {
      colDefs.push({ key: "list", label: "LİSTE FİYATI", w: 23, align: "right", st: { textColor: SLATE } });
      colDefs.push({ key: "disc", label: "İSK.", w: 15.5, align: "center", st: { fontStyle: "bold", textColor: NAVY } });
    }
    colDefs.push({ key: "unit", label: "BİRİM FİYAT", w: 24, align: "right", st: {} });
    colDefs.push({ key: "tot", label: "TUTAR", w: 26, align: "right", st: { fontStyle: "bold", textColor: NAVY } });
    const fixedW = colDefs.reduce((a, c) => a + (c.w || 0), 0);
    const pad = compact ? 0.7 : 1.5;
    const columnStyles = {};
    colDefs.forEach((c, i) => { columnStyles[i] = { cellWidth: c.w == null ? W - fixedW : c.w, halign: c.align, cellPadding: { top: pad, bottom: pad, left: 1.6, right: 1.6 }, ...c.st }; });
    const body = items.map((it, i) => colDefs.map((c) => {
      switch (c.key) {
        case "no": return String(i + 1);
        case "code": return String(it.code || "").trim() || "—";
        case "name": return itemDisplayName(it, { showBrand: cols.brand });
        case "qty": return `${NUM(it.quantity)} ${unitLabel(it.unit)}`;
        case "list": return money(it.list_price != null ? it.list_price : unitGrossAdj[i]);
        case "disc": return Number(it.sale_discount) ? `%${NUM(it.sale_discount)}` : "—";
        case "unit": return money(unitGrossAdj[i]);
        case "tot": return money(lineTotals[i]);
        default: return "";
      }
    }));
    const lastCol = colDefs.length - 1;
    autoTable(pdf, {
      head: [colDefs.map((c) => ({ content: c.label, styles: { halign: c.align } }))],
      body: body.length ? body : [[{ content: "Kalem bulunmuyor", colSpan: colDefs.length, styles: { halign: "center", textColor: SLATE } }]],
      startY: y0 + infoH + (compact ? 4 : 6), margin: { left: M, right: M, top: HEAD2 + 5, bottom: FOOT + 5 }, rowPageBreak: "avoid", theme: "grid",
      styles: { font: F.body, fontSize: base, cellPadding: { top: pad, bottom: pad, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: 0.2, overflow: "linebreak", valign: "middle", minCellHeight: base * (compact ? 0.72 : 1.05) },
      headStyles: { font: F.body, fontStyle: "bold", fillColor: WASH, textColor: NAVY, fontSize: base - 0.6, valign: "middle", minCellHeight: 9 * k, lineWidth: 0, cellPadding: { top: 2 * k, bottom: 2 * k, left: 1.6, right: 1.6 } },
      alternateRowStyles: { fillColor: [250, 251, 253] }, columnStyles,
      didDrawCell: (d) => { if (d.section === "head" && d.column.index === lastCol) { fill(NAVY); pdf.rect(M, d.cell.y + d.cell.height - 0.5, W, 0.5, "F"); } },
      didDrawPage: (d) => drawHeader(d.pageNumber),
    });

    // ---------- Not + Teslimat (sol) · Genel Toplam / ödeme / iade (sağ) ----------
    let y = pdf.lastAutoTable.finalY + 5 * k;
    const bottomLimit = pageH - FOOT - 3;
    const ensure = (need) => { if (y + need > bottomLimit) { pdf.addPage(); drawHeader(pdf.getNumberOfPages()); y = HEAD2 + 7; } };
    const rightW = 78, rightX = M + W - rightW, leftW = W - rightW - 6;
    const prh = 5.2 * k, retItemH = 4 * k;
    const payH = payRows.length ? 5 * k + payRows.length * prh + 2 : 0;
    const cashH = cashRows.length ? 5 * k + cashRows.length * prh + 2 : 0;
    const retH = hasReturn ? (32 * k + Math.max(1, retAgg.length) * retItemH) : 0;
    const boxH = 16 * k, totalsH = boxH + payH + cashH + retH + 2;
    const noteTxt = String(s.note || "").trim();
    T(F.body, "bold", 7.8 * k + 0.4, NAVY); const noteLines = noteTxt ? pdf.splitTextToSize(noteTxt, leftW - 12) : [];
    const noteH = noteLines.length ? 9.5 * k + noteLines.length * 3.9 * k + 2 : 0;
    T(F.body, "normal", 7.4 * k + 0.3, SLATE); const deliv = pdf.splitTextToSize(DELIVERY_TEXT, leftW - 12);
    const delivH = 8.5 * k + deliv.length * 3.7 * k + 2.5;
    const leftH = noteH + (noteH ? 3 * k : 0) + delivH;
    const blockH = Math.max(leftH, totalsH);
    ensure(blockH);
    let ly = y;
    if (noteH) {
      fill(WHITE); stroke(GOLD, 0.6); pdf.roundedRect(M, ly, leftW, noteH, 2.5, 2.5, "FD");
      fill(GOLD); pdf.roundedRect(M + 2, ly + 3, 1.3, noteH - 6, 0.6, 0.6, "F");
      T(F.body, "bold", 6.2 * k + 0.4, GOLD_D); pdf.text("ÖZEL NOT", M + 6, ly + 5 * k, { charSpace: 0.5 });
      T(F.body, "bold", 7.8 * k + 0.4, NAVY); pdf.text(noteLines, M + 6, ly + 9.6 * k, { lineHeightFactor: 1.4 });
      ly += noteH + 3 * k;
    }
    fill(WASH); pdf.roundedRect(M, ly, leftW, delivH, 2.5, 2.5, "F");
    T(F.body, "bold", 6.2 * k + 0.4, NAVY); pdf.text("TESLİMAT", M + 6, ly + 5 * k, { charSpace: 0.5 });
    T(F.body, "normal", 7.4 * k + 0.3, SLATE); pdf.text(deliv, M + 6, ly + 9.4 * k, { lineHeightFactor: 1.38 });

    let ty = y;
    fill(WHITE); stroke(NAVY, 0.6); pdf.roundedRect(rightX, ty, rightW, boxH, 2.5, 2.5, "FD");
    fill(GOLD); pdf.roundedRect(rightX + 2.4, ty + 3.5, 1.4, boxH - 7, 0.6, 0.6, "F");
    T(F.head, "bold", 7.4 * k + 0.4, NAVY); pdf.text("GENEL TOPLAM", rightX + 7, ty + boxH / 2 + (vatNote ? -1.4 : 1.2), { charSpace: 0.5 });
    if (vatNote) { T(F.body, "normal", 6.4 * k + 0.3, SLATE); pdf.text(ellipsize(vatNote, rightW - 46), rightX + 7, ty + boxH / 2 + 3.6); }
    const amt = money(s.grand_total);
    const afs = shrink(amt, rightW - 44, F.head, "bold", 15 * k, 9);
    T(F.head, "bold", afs, NAVY); pdf.text(amt, rightX + rightW - 4.5, ty + boxH / 2 + 2.2, { align: "right" });
    let py = ty + boxH + 3;
    const miniRows = (title, rowsArr, colorFn) => {
      T(F.body, "bold", 5.8 * k + 0.3, MUTED); pdf.text(title, rightX + 4.5, py + 2, { charSpace: 0.5 }); py += 4.2 * k;
      rowsArr.forEach((r, i) => {
        const [l, v] = r, [lc, vc, bold] = colorFn(r, i);
        T(F.body, bold ? "bold" : "normal", 7.2 * k + 0.3, lc); pdf.text(l, rightX + 4.5, py + 2.6);
        T(F.body, "bold", 7.4 * k + 0.3, vc); pdf.text(v, rightX + rightW - 4.5, py + 2.6, { align: "right" });
        py += prh;
      });
      py += 2;
    };
    if (payRows.length) miniRows("ÖDEME DAĞILIMI", payRows.map(([l, v, ver]) => [ver ? `${l} (ödeme alınmadı)` : l, v, ver]), (r) => (r[2] ? [RED, RED, true] : [SLATE, INK, false]));
    if (cashRows.length) miniRows("NAKİT", cashRows, (r, i) => (i === 1 ? [GOLD_D, GOLD_D, false] : [SLATE, INK, false]));
    if (hasReturn) {
      const rb = retH - 2;
      fill([254, 242, 242]); stroke([239, 68, 68], 0.5); pdf.roundedRect(rightX, py, rightW, rb, 2, 2, "FD");
      fill([239, 68, 68]); pdf.roundedRect(rightX + 2.4, py + 3.5, 1.3, rb - 7, 0.6, 0.6, "F");
      T(F.body, "bold", 6.2 * k + 0.3, RED); pdf.text("İADE ÖZETİ", rightX + 7, py + 4.8, { charSpace: 0.5 });
      let ry = py + 9 * k;
      T(F.body, "bold", 5.6, [201, 108, 108]); pdf.text("İADE EDİLEN ÜRÜNLER", rightX + 7, ry - 0.2, { charSpace: 0.4 }); ry += 3.6 * k;
      (retAgg.length ? retAgg : [{ name: "-", qty: 0 }]).forEach((it) => {
        T(F.body, "normal", 7 * k + 0.3, SLATE); pdf.text(ellipsize(it.name || "-", rightW - 26), rightX + 7, ry);
        T(F.body, "bold", 7 * k + 0.3, RED); pdf.text(`${NUM(it.qty)} ad`, rightX + rightW - 4.5, ry, { align: "right" });
        ry += retItemH;
      });
      ry += 2;
      stroke([252, 165, 165], 0.4); pdf.line(rightX + 7, ry, rightX + rightW - 4.5, ry);
      T(F.body, "normal", 7.2 * k, SLATE); pdf.text("Orijinal Toplam", rightX + 7, ry + 4 * k);
      T(F.body, "bold", 7.4 * k, INK); pdf.text(money(grandTotal), rightX + rightW - 4.5, ry + 4 * k, { align: "right" });
      T(F.body, "normal", 7.2 * k, RED); pdf.text("İade Edilen", rightX + 7, ry + 8 * k);
      T(F.body, "bold", 7.4 * k, RED); pdf.text(`− ${money(returnedTotal)}`, rightX + rightW - 4.5, ry + 8 * k, { align: "right" });
      stroke([252, 165, 165], 0.5); pdf.line(rightX + 7, ry + 10 * k, rightX + rightW - 4.5, ry + 10 * k);
      T(F.body, "bold", 7 * k, NAVY); pdf.text("KALAN GENEL TOPLAM", rightX + 7, ry + 14 * k);
      T(F.head, "bold", 9.5 * k, NAVY); pdf.text(money(remaining), rightX + rightW - 4.5, ry + 14 * k, { align: "right" });
    }
    y += blockH + 6 * k;

    // ---------- İmzalar (Teslim Eden / Teslim Alan) ----------
    ensure(22 * k);
    const sy = y + 9 * k;
    const sigW = 70;
    [[M, "TESLİM EDEN", `${COMPANY.name} · ${s.user_name || ""}`], [M + W - sigW, "TESLİM ALAN (İMZA)", s.title || "Perakende Müşteri"]].forEach(([x, l, sub]) => {
      stroke(NAVY, 0.4); pdf.line(x, sy, x + sigW, sy);
      T(F.body, "bold", 6.8 * k + 0.3, NAVY); pdf.text(l, x, sy + 4.2 * k, { charSpace: 0.4 });
      T(F.body, "normal", 6.8 * k + 0.3, SLATE); pdf.text(ellipsize(sub, sigW), x, sy + 8.2 * k);
    });
    y = sy + 12 * k;

    // ---------- Teşekkür + güven rozetleri — son sayfanın EN ALTINA yakın; teşekkür yazısı rozetlerin hemen üstünde ----------
    const badges = [["truck", "HIZLI", "TEDARİK"], ["box", "GENİŞ ÜRÜN", "YELPAZESİ"], ["shield", "ORİJİNAL", "ÜRÜNLER"], ["headset", "TEKNİK", "DESTEK"]];
    const isz = 7.6 * k, badgeH = isz + 4 * k + 0.9, thanksH = 7 * k;
    ensure(badgeH + thanksH);
    y = Math.max(y, bottomLimit - badgeH - thanksH);
    T(F.head, "normal", 7.8 * k, NAVY); pdf.text("Bizi tercih ettiğiniz için teşekkür ederiz.", pageW / 2, y + 4.2 * k, { align: "center" });
    y += thanksH;
    stroke(NAVY, 0.5); pdf.line(M, y, pageW - M, y);
    y += 4 * k;
    const cw2 = W / badges.length;
    T(F.head, "normal", 7 * k, NAVY);
    badges.forEach(([ic, l1, l2], i) => {
      const cx = M + i * cw2;
      const textW = Math.max(pdf.getTextWidth(l1), pdf.getTextWidth(l2));
      const sx = cx + (cw2 - (isz + 2.8 + textW)) / 2;
      icon(ic, sx, y, isz);
      pdf.text(l1, sx + isz + 2.8, y + isz * 0.42); pdf.text(l2, sx + isz + 2.8, y + isz * 0.86);
      if (i > 0) { stroke(LINE, 0.35); pdf.line(cx, y + 0.4, cx, y + isz - 0.4); }
    });

    const n = pdf.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      pdf.setPage(i);
      T(F.body, "normal", 6, SLATE); pdf.text(`Sayfa ${i} / ${n}`, pageW - M, pageH - FOOT + 8.7, { align: "right" });
    }
    return pdf;
  };

  if (opts.singlePage) {
    let pdf;
    for (const [fs, compact] of FIT_STEPS) { pdf = await render(fs, compact); if (pdf.getNumberOfPages() === 1) break; }
    return pdf;
  }
  return render(8.4, false);
};

// Bir satışın kâr/marj özeti (fiş önizleme — yalnız yönetici)
export const saleProfit = (s) => {
  let cost = 0, net = 0;
  (s?.items || []).forEach((it) => {
    const q = Number(it.quantity) || 0;
    cost += (Number(it.unit_cost) || 0) * q;
    net += it.line_net != null ? Number(it.line_net) : (Number(it.unit_net) || 0) * q;
  });
  const profit = r2(net - cost);
  return { cost: r2(cost), net: r2(net), profit, margin: net > 0 ? r2((profit / net) * 100) : 0 };
};
